import { isMemberMode } from './viewMode';
/**
 * Thin fetch client for the web app. Uses httpOnly cookies (credentials:
 * 'include') and transparently attempts a token refresh once on 401. The future
 * mobile app talks to the same endpoints but with bearer tokens instead.
 */
import type { ApiError as ApiErrorShape } from '@gwc/shared';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';
const V2_BASE = BASE.replace(/\/v1\/?$/, '/v2') || '/api/v2';

/** Endpoints that must not trigger a silent refresh on 401 (would loop or is pointless). */
const NO_AUTO_REFRESH = new Set([
  '/auth/login',
  '/auth/refresh',
  '/auth/setup',
  '/auth/verify-otp',
  '/auth/accept-invite',
  '/auth/invite-preview',
  '/auth/resend-invite',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/session',
  '/auth/logout',
]);

function requestPath(path: string): string {
  return path.split('?')[0] ?? path;
}

function canAutoRefresh(path: string): boolean {
  return !NO_AUTO_REFRESH.has(requestPath(path));
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions extends RequestInit {
  _retry?: boolean;
}

/**
 * The single in-flight refresh.
 *
 * Without this, every 401 fires its own POST /auth/refresh. A page load (or a
 * redeploy) issues half a dozen queries at once, they all 401 together on an
 * expired access token, and they all reach for the refresh endpoint carrying the
 * same cookie. The first rotates it; the rest arrive with a token that is by then
 * revoked, the server reads that as token REUSE, and revokes the whole family —
 * logging the member out. Refreshing exactly once, and letting everyone else wait
 * on that one call, removes the race at the source.
 */
let inFlightRefresh: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      if (res.ok) return true;

      // Another tab (which has its own JS context, and so its own in-flight
      // refresh) beat us to the rotation. The cookie jar is shared, so we already
      // hold the fresh cookies it was issued — retry the original request rather
      // than treating this as a failed session.
      if (res.status === 401) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        if (body?.error === 'refresh_raced') return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      // Cleared only once the call settles, so callers that arrive mid-flight
      // await this one instead of starting another.
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // Only send a JSON content-type when there's actually a JSON body — Fastify
  // rejects an empty JSON body (e.g. bodyless DELETE/POST) with a 400, and for
  // FormData the browser must set its own multipart boundary.
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const hasJsonBody = options.body != null && !isForm;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      ...options,
      headers: {
        'x-gwc-view-mode': isMemberMode() ? 'member' : 'admin',
        ...(hasJsonBody ? { 'content-type': 'application/json' } : {}),
        ...(options.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError(
      0,
      'network_error',
      'Could not reach the server. Check that the API is running and your network connection.',
    );
  }

  if (res.status === 401 && !options._retry && canAutoRefresh(path)) {
    if (await tryRefresh()) return request<T>(path, { ...options, _retry: true });
  }

  if (res.status === 304) {
    return null as T;
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ApiError(
        res.status,
        'parse_error',
        res.ok
          ? 'Server returned an invalid response.'
          : `Server error (${res.status}): the response was not JSON. The API may be down or misconfigured.`,
      );
    }
  }
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiErrorShape>;
    throw new ApiError(
      res.status,
      err.error ?? 'error',
      err.message ?? res.statusText,
      err.details,
    );
  }
  return data as T;
}

/**
 * Multipart upload with progress — XHR because fetch cannot report upload
 * progress. Same cookie, view-mode header and single-retry-on-401 behaviour as
 * `request()`.
 */
export function uploadWithProgress<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
  _retry = false,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('x-gwc-view-mode', isMemberMode() ? 'member' : 'admin');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () =>
      reject(new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.'));
    xhr.onload = async () => {
      if (xhr.status === 401 && !_retry && canAutoRefresh(path) && (await tryRefresh())) {
        uploadWithProgress<T>(path, form, onProgress, true).then(resolve, reject);
        return;
      }
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        reject(new ApiError(xhr.status, 'parse_error', 'Server returned an invalid response.'));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data as T);
      } else {
        const err = (data ?? {}) as Partial<ApiErrorShape>;
        reject(new ApiError(xhr.status, err.error ?? 'error', err.message ?? xhr.statusText, err.details));
      }
    };
    xhr.send(form);
  });
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Multipart upload — pass a FormData; the browser sets the boundary itself. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
  /**
   * Fetch a file (CSV export). A bare <a download> can't carry the session cookie
   * reliably cross-origin, so we fetch it and hand the browser a blob instead.
   */
  blob: async (path: string): Promise<Blob> => {
    const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
    if (!res.ok) throw new ApiError(res.status, 'export_failed', 'Export failed');
    return res.blob();
  },
};

/** v2 API client — responses use `{ data, meta? }` envelope. */
export const apiV2 = {
  get: async <T>(path: string) => {
    const envelope = await rawRequest<{ data: T; meta?: unknown }>(`${V2_BASE}${path}`);
    return envelope.data;
  },
  post: async <T>(path: string, body?: unknown) => {
    const envelope = await rawRequest<{ data: T; meta?: unknown }>(`${V2_BASE}${path}`, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return envelope.data;
  },
  patch: async <T>(path: string, body?: unknown) => {
    const envelope = await rawRequest<{ data: T; meta?: unknown }>(`${V2_BASE}${path}`, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return envelope.data;
  },
  list: async <T>(path: string) => {
    const envelope = await rawRequest<{
      data: T;
      meta: { page: number; pageSize: number; total: number };
    }>(`${V2_BASE}${path}`);
    return { data: envelope.data, meta: envelope.meta };
  },
};

async function rawRequest<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const path = url.replace(V2_BASE, '');
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const hasJsonBody = options.body != null && !isForm;
  let res: Response;
  try {
    res = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        'x-gwc-view-mode': isMemberMode() ? 'member' : 'admin',
        ...(hasJsonBody ? { 'content-type': 'application/json' } : {}),
        ...(options.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the server.');
  }

  if (res.status === 401 && !options._retry && canAutoRefresh(path)) {
    if (await tryRefresh()) return rawRequest<T>(url, { ...options, _retry: true });
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ApiError(res.status, 'parse_error', 'Invalid server response.');
    }
  }
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiErrorShape>;
    throw new ApiError(
      res.status,
      err.error ?? 'error',
      err.message ?? res.statusText,
      err.details,
    );
  }
  return data as T;
}

/** Absolute-ish URL for a same-origin API path (e.g. a file download link). */
export const apiUrl = (path: string): string => `${BASE}${path}`;
